import { Injectable, inject , signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AccountService } from '../account/account.service';
import { SettingsService } from '../account/settings.service';
import { WaypointService } from '../core/waypoint.service';
import { UtilsService } from '../utils/utils.service';
import { WebviewService } from '../platform/webview.service';
import { ECar, ECarSettings } from '../../models/ecar';
import { ECarType } from '../../models/ecar-type';
import { VEHICLE_IMAGE_URL } from '../core/http.service';
import { DisplayMode } from '../../models/display-mode';
import { StackedModalsService } from '../modal/stacked-modals.service';
import { WelcomeComponent } from '../../components/modals/info/welcome/welcome.component';
import { VehicleOptionsDialogComponent } from '../../components/modals/vehicle-options-dialog/vehicle-options-dialog.component';
import { WidgetService } from '../platform/widget.service';

@Injectable({ providedIn: 'root' })
export class VehicleService {

  public ECars: ECarType[] = [];
  public CarTypes: { name: string, subtypes: string[] }[] = [];
  public ObservableECarsLoaded = new BehaviorSubject<{ ECars: ECarType[], CarTypes: { name: string, subtypes: string[] }[] } | null>(null);

  private SelectedCar: ECarType = new ECarType({
    id: -1, carWeight: null!, chargerTypes: null!, designCapacity: null!, dragCoefficient: null!, dragCross: null!, name: null!,
    range: null!, factoryRange: null!, factoryRangeSource: null!, topSpeed: null!, totalPower: null!, icon: '',
    chargePower: null!, fastChargePower: null!, type: null!, subtype: '', greenCarsLink: ''
  });
  /** Signal-backed: consumers read it via getSelectedCarIcon() and re-render when the skin changes. */
  private readonly SelectedCarIcon = signal<string>('');
  public ObservableSelectedCar = new BehaviorSubject<ECar | null>(null);

  private ChargingStations: boolean[] = [];
  public ObservableChargingStations = new BehaviorSubject<boolean[]>([]);

  private ECarSettings: ECarSettings | null = null;
  public ObservableECarSettings = new BehaviorSubject<ECarSettings | null>(null);

  public readonly ObservableBatteryChanged = new BehaviorSubject<number | null>(null);
  public readonly ObservableTripRecordingChanged = new BehaviorSubject<number>(1);


  private readonly accountService = inject(AccountService);
  private readonly settingsService = inject(SettingsService);
  private readonly waypointService = inject(WaypointService);
  private readonly utilsService = inject(UtilsService);
  private readonly webviewService = inject(WebviewService);
  private readonly stackedModalsService = inject(StackedModalsService);
  private readonly widgetService = inject(WidgetService);

  constructor() {
    this.SelectedCarIcon.set(VEHICLE_IMAGE_URL
      .replace('$skin', this.settingsService.getMap().Skin)
      .replace('$icon_name', 'car_icon_default'));

    this.accountService.ObservableSelectedUserCar.subscribe((resp: ECar | null) => {
      if (resp?.ECarType) {
        this.ObservableSelectedCar.next(resp);
        this.setCarIcon(resp.ECarType);

        const chargingStations = [false, false, false, false, false, false];
        for (const chargerType of resp.ECarType.ChargerTypes) {
          chargingStations[chargerType - 1] = true;
        }
        this.setChargingStations(chargingStations);

        if (resp.settings) {
          if (this.waypointService.getSelectedDisplayMode() === DisplayMode.ROUTE) {
            resp.settings.batterySafetyLimit = resp.settings.batterySafetyLimitRoute;
          } else {
            resp.settings.batterySafetyLimit = resp.settings.batterySafetyLimitRha;
          }
          this.setECarSettings(resp.settings);
          if (resp.settings.tripRecording != undefined) {
            this.ObservableTripRecordingChanged.next(resp.settings.tripRecording);
          }
        }
      }
    });

    this.accountService.ObservableIsAuthorized.subscribe((resp) => {
      if (resp === false) {
        this.initCar();
      }
    });

    this.settingsService.ObservableMap.subscribe((resp) => {
      if (resp && this.SelectedCar) {
        this.setCarIcon(this.getSelectedCar());
      }
    });

    this.ObservableBatteryChanged.subscribe((resp) => {
      if (this.accountService.getIsAuthorized() && resp) {
        const userCar = this.accountService.user.getSelectedUserCar();
        if (userCar && userCar.settings.batteryLevel != resp) {
          userCar.settings.batteryLevel = resp;
          this.accountService.editVehicle(userCar.userVehicleID, null, null, null, JSON.stringify(userCar.settings), false).subscribe();
        }
      }
      if (!this.accountService.getIsAuthorized() && resp) {
        localStorage.setItem('batteryLevel', resp.toString());
      }
    });

    this.initWebViewSetBattery();
  }

  public initAllVehicles(allVehicles: ECarType[]): void {
    this.ECars = allVehicles;
    this.CarTypes = this.utilsService.collectCarTypes(this.ECars);
    this.initCar();
    this.ObservableECarsLoaded.next({ ECars: this.ECars, CarTypes: this.CarTypes });
  }

  public initCar(): void {
    if (this.widgetService.isWidget()) {
      this.initWidgetCar();
    }
    else {
      this.initStoredCar();
    }

    const chargingStations = this.resolveChargingStations();

    this.setChargingStations(chargingStations);

    const ecar: ECarType = this.getSelectedCar();
    if (ecar) {
      ecar.ChargerTypes = [];
      for (let i = 0; i < chargingStations.length; i++) {
        if (chargingStations[i]) ecar.ChargerTypes.push(i + 1);
      }
      this.setSelectedCar(ecar, true);
    }

    this.applyWidgetCarSettings();
  }

  // Widget mode: no welcome / car-selector dialogs. The car comes from `carid`,
  // then from the stored selection, then the default.
  private initWidgetCar(): void {
    const widgetCarId = this.widgetService.getCarParams().carId;
    let selectedCar: ECarType | null = widgetCarId == null ? null : this.getCarByID(widgetCarId) ?? null;
    if (!selectedCar) {
      const selectedCarId = localStorage.getItem('selectedCarId');
      selectedCar = selectedCarId ? this.ECars.find((ecar) => ecar.Id === Number.parseInt(selectedCarId)) ?? null : null;
    }
    if (selectedCar) {
      this.setSelectedCar(selectedCar, false);
    } else {
      this.selectDefaultCar();
    }
  }

  // Full site: restore the stored car, or fall back to the default and ask the user to pick one.
  private initStoredCar(): void {
    const selectedCarName = localStorage.getItem('selectedCar'); // legacy check for older non-auth users
    const selectedCarId = localStorage.getItem('selectedCarId');

    if (selectedCarName == null && selectedCarId == null) {
      if (!document.URL.includes('carid')) {
        this.stackedModalsService.openModal(WelcomeComponent, {
          autoFocus: false,
          panelClass: ['dialog-snap-to-fullscreen'],
          data: { firstInit: true },
        });
        this.openVehicleOptionsDialog();
      }
      this.selectDefaultCar();
      return;
    }

    const selectedCar = selectedCarId ? this.ECars.find((ecar) => ecar.Id === Number.parseInt(selectedCarId)) : undefined;
    if (selectedCar) {
      this.setSelectedCar(selectedCar, false);
    } else {
      this.selectDefaultCar();
      this.openVehicleOptionsDialog();
    }
  }

  private openVehicleOptionsDialog(): void {
    this.stackedModalsService.openModal(VehicleOptionsDialogComponent, {
      data: {},
      autoFocus: false,
      panelClass: ['car-selector-outer', 'dialog-snap-to-fullscreen'],
    });
  }

  // Charger types: from the widget URL, else the stored selection, else the selected car's own types.
  private resolveChargingStations(): boolean[] {
    const chargingStationsStorage: string | null = localStorage.getItem('chargingStations');

    const widgetChargerTypes = this.widgetService.isWidget() ? this.widgetService.getCarParams().chargerTypes : null;
    if (widgetChargerTypes) {
      return widgetChargerTypes;
    }

    const chargingStations: boolean[] = [];
    if (chargingStationsStorage) {
      const csArray = chargingStationsStorage.split(',');
      for (const cs of csArray) {
        if (cs === 'true') chargingStations.push(true);
        if (cs === 'false') chargingStations.push(false);
      }
      return chargingStations;
    }

    const defaultStations = [false, false, false, false, false, false];
    for (const chargerType of this.SelectedCar.ChargerTypes) {
      defaultStations[chargerType - 1] = true;
    }
    return defaultStations;
  }

  /**
   * Widget mode: merge the car parameters given in the URL into the current
   * ECarSettings and emit. Idempotent — called after initCar() and again once the
   * vehicle panel has restored its own stored values, so the URL wins either way.
   */
  public applyWidgetCarSettings(): void {
    if (!this.widgetService.isWidget()) return;
    const overrides = this.widgetService.getCarParams().settings;
    if (Object.keys(overrides).length === 0) return;

    const base: ECarSettings = this.ECarSettings ?? {
      batteryLevel: 73, weight: 2, tirePressure: 2.3, userBehavior: 50, speedLimiter: 100,
      batterySafetyLimitRha: 50, batterySafetyLimitRoute: 15, minBatteryAtDestination: 10,
    };
    const merged: ECarSettings = { ...base, ...overrides };
    merged.batterySafetyLimit = this.waypointService.getSelectedDisplayMode() === DisplayMode.ROUTE
      ? merged.batterySafetyLimitRoute : merged.batterySafetyLimitRha;
    this.setECarSettings(merged);
  }

  public selectDefaultCar(): void {
    const defCarId: number = this.utilsService.defaultCarIndex;
    const defCar: ECarType | undefined = this.ECars.find((ecar) => ecar.Id === defCarId);
    if (defCar) {
      this.setSelectedCar(defCar, false);
    }
  }

  public getCarByID(id: number): ECarType | undefined {
    return this.ECars.find((ecar) => ecar.Id === id);
  }

  public setSelectedCar(ecar: ECarType, setCarChargingStations: boolean): void {
    this.SelectedCar = new ECarType({
      id: ecar.Id, carWeight: ecar.CarWeight, chargerTypes: ecar.ChargerTypes, designCapacity: ecar.DesignCapacity,
      dragCoefficient: ecar.DragCoefficient, dragCross: ecar.DragCross, name: ecar.Name, range: ecar.Range,
      factoryRange: ecar.FactoryRange, factoryRangeSource: ecar.FactoryRangeSource, topSpeed: ecar.TopSpeed,
      totalPower: ecar.TotalPower, icon: ecar.Icon, chargePower: ecar.ChargePower, fastChargePower: ecar.FastChargePower,
      type: ecar.Type, subtype: ecar.Subtype, greenCarsLink: ecar.GreenCarsLink
    });

    if (ecar.Icon) {
      this.setCarIcon(ecar);
    }

    if (setCarChargingStations) {
      const chargingStations = [false, false, false, false, false, false];
      for (const chargerType of this.SelectedCar.ChargerTypes) {
        chargingStations[chargerType - 1] = true;
      }
      this.setChargingStations(chargingStations);
    }

    localStorage.setItem('selectedCarId', this.SelectedCar.Id.toString());

    const nullUserEcar = new ECar();
    nullUserEcar.ECarType = ecar;
    nullUserEcar.name = '';
    nullUserEcar.selected = true;
    nullUserEcar.userVehicleID = -1;
    this.ObservableSelectedCar.next(nullUserEcar);
  }

  public getSelectedCar(): ECarType {
    if (this.accountService.getIsAuthorized() && this.accountService.user.getSelectedCar()) {
      return this.accountService.user.getSelectedCar()!;
    }
    return this.SelectedCar;
  }

  public getSelectedCarIcon(): string {
    return this.SelectedCarIcon();
  }

  public setChargingStations(params: boolean[]): void {
    this.ChargingStations = params;
    this.ObservableChargingStations.next(params);
  }

  public getChargingStations(): boolean[] {
    return this.ChargingStations;
  }

  private setCarIcon(ecar: ECarType): void {
    if (ecar.Icon.length > 0) {
      this.SelectedCarIcon.set(VEHICLE_IMAGE_URL
        .replace('$skin', this.settingsService.getMap().Skin)
        .replace('$icon_name', ecar.Icon));
    } else {
      this.SelectedCarIcon.set(VEHICLE_IMAGE_URL
        .replace('$skin', this.settingsService.getMap().Skin)
        .replace('$icon_name', 'car_icon_default'));
    }
  }

  public getECarSettings(): ECarSettings | null { return this.ECarSettings; }

  public setECarSettings(params: ECarSettings): void {
    this.ECarSettings = params;
    this.ObservableECarSettings.next(params);
  }

  public getBattery(): number { 
    return this.ECarSettings?.batteryLevel ?? 73;
  }

  public setBattery(batteryLevel: number): void {
    if (batteryLevel && this.ECarSettings) {
      this.ECarSettings.batteryLevel = batteryLevel;
      this.ecarSettingsUpdate();
    }
  }

  // Updates battery level in ECarSettings without emitting ObservableECarSettings,
  // so the change does not trigger a new calculation. Use when syncing back a
  // server-returned SOC value for display purposes only.
  public setBatterySilent(batteryLevel: number): void {
    if (batteryLevel && this.ECarSettings) {
      this.ECarSettings.batteryLevel = batteryLevel;
    }
  }

  public setWeight(weight: number): void {
    if (this.ECarSettings) {
      this.ECarSettings.weight = weight;
      this.ecarSettingsUpdate();
    }
  }

  public getBatteryStateOfHealth(): number {
    return this.ECarSettings?.batteryStateOfHealth ?? 100;
  }

  public setBatteryStateOfHealth(value: number): void {
    if (this.ECarSettings) {
      this.ECarSettings.batteryStateOfHealth = value;
      this.ecarSettingsUpdate();
    }
  }

  public getFastChargeLimit(): number | undefined {
    return this.ECarSettings?.fastChargeLimit;
  }

  public setFastChargeLimit(value: number): void {
    if (this.ECarSettings) {
      this.ECarSettings.fastChargeLimit = value;
      this.ecarSettingsUpdate();
    }
  }

  public getTripRecording(): number {
    return this.ECarSettings?.tripRecording ?? this.ObservableTripRecordingChanged.value;
  }

  public setTripRecording(value: number): void {
    if (this.ECarSettings) {
      this.ECarSettings.tripRecording = value;
      this.ecarSettingsUpdate();
    }
    this.ObservableTripRecordingChanged.next(value);
  }

  public setSliderRangeInputParams(params: any): void {
    if (this.ECarSettings?.batteryStateOfHealth) {
      params.batteryStateOfHealth = this.ECarSettings.batteryStateOfHealth;
    }
    if (this.ECarSettings?.fastChargeLimit) {
      params.fastChargeLimit = this.ECarSettings.fastChargeLimit;
    }
    this.ECarSettings = params;
    this.ecarSettingsUpdate();
  }

  public loadCarSettingsCookies(): void {
    let batteryStateOfHealth, fastChargeLimit;
    try {
      const batteryData = localStorage.getItem('batteryStateOfHealth');
      batteryStateOfHealth = batteryData ? JSON.parse(batteryData) : null;
    } catch {
      // Unreadable or corrupt stored value: keep the current setting.
    }
    if (batteryStateOfHealth != null) {
      this.setBatteryStateOfHealth(batteryStateOfHealth);
    }
    try {
      const chargeData = localStorage.getItem('fastChargeLimit');
      fastChargeLimit = chargeData ? JSON.parse(chargeData) : null;
    } catch {
      // Unreadable or corrupt stored value: keep the current setting.
    }
    if (fastChargeLimit != null) {
      this.setFastChargeLimit(fastChargeLimit);
    }
    let tripRecording;
    try {
      const tripRecordingData = localStorage.getItem('tripRecording');
      tripRecording = tripRecordingData ? JSON.parse(tripRecordingData) : null;
    } catch {
      // Unreadable or corrupt stored value: keep the current setting.
    }
    if (tripRecording != null) {
      this.setTripRecording(tripRecording);
    }
  }

  public initWebViewSetBattery(): void {
    this.webviewService.InitSetBattery((battery) => {
      this.setBattery(battery);
      this.ecarSettingsUpdate();
    });
  }

  public ecarSettingsUpdate(): void {
    this.ObservableECarSettings.next(this.ECarSettings ? { ...this.ECarSettings } : null);
  }
}
